/*
 * Case studies (index + detail) page behavior, on top of content-system.js.
 * Index: story-row rhythm by visible position, mounting-chip disabling within
 * the active industry, "Showing x of y" progress bar, removable chips in the
 * empty state. Detail: gallery filmstrip (counter, arrows, segments, lightbox).
 * Progressive enhancement: without JS every card and slide is visible.
 */
(() => {
  if (window.csCaseStudies) return;

  const tokens = (value) => String(value || '').toLowerCase().split(/\s+/).filter(Boolean);

  /* ---------------------------------------------------------------------------
     Index
     --------------------------------------------------------------------------- */
  const RHYTHM = 8;

  const updateIndex = (root, state) => {
    if (!root || !state) return;
    const items = [...root.querySelectorAll('[data-cs-item]')];

    // 1. Layout rhythm by position in the visible (filtered) list.
    let pos = 0;
    items.forEach((item) => {
      if (item.hidden) return;
      const slot = pos % RHYTHM;
      const featured = slot === 0 || slot === 5;
      item.classList.toggle('cs-story--featured', featured);
      item.classList.toggle('cs-story--reverse', slot === 5);
      pos += 1;
    });

    // 2. Disable mounting chips with 0 studies inside the active industry.
    const industry = state.industry || 'all';
    const inIndustry = items.filter((item) => industry === 'all' || tokens(item.dataset.industry).includes(industry));
    const scopes = [root, ...document.querySelectorAll(`[data-cs-for~="${CSS.escape(root.id)}"]`)];
    scopes.forEach((scope) =>
      scope.querySelectorAll('[data-cs-filter="type"]').forEach((chip) => {
        const value = String(chip.dataset.value).toLowerCase();
        const count = value === 'all' ? inIndustry.length : inIndustry.filter((item) => tokens(item.dataset.type).includes(value)).length;
        const disabled = count === 0 && !chip.classList.contains('is-active');
        chip.classList.toggle('is-disabled', disabled);
        if (disabled) {
          chip.setAttribute('aria-disabled', 'true');
          chip.tabIndex = -1;
        } else {
          chip.removeAttribute('aria-disabled');
          chip.removeAttribute('tabindex');
        }
      })
    );

    // 3. Progress bar under "Showing x of y".
    const shown = items.filter((item) => !item.hidden).length;
    const total = Number(root.dataset.csMatched || shown);
    root.querySelectorAll('[data-csx-progress]').forEach((bar) => {
      bar.style.setProperty('--csx-progress', total ? Math.min(1, shown / total).toFixed(4) : '0');
    });
    const more = root.querySelector('.csx-more');
    if (more) more.hidden = total === 0;
  };

  const renderActiveChips = (root, detail) => {
    const holder = root.querySelector('[data-csx-active-chips]');
    if (!holder) return;
    holder.replaceChildren();
    // industry first, then mounting type (states frame #5)
    const order = (k) => (k === 'industry' ? 0 : k === 'type' ? 1 : 2);
    Object.entries(detail.state).sort((a, b) => order(a[0]) - order(b[0])).forEach(([key, value]) => {
      if (!value || value === 'all') return;
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'csx-chip';
      chip.dataset.key = key;
      chip.innerHTML = '<span></span><span aria-hidden="true">&times;</span>';
      chip.firstChild.textContent = detail.labels[key] || value;
      chip.setAttribute('aria-label', `Remove filter: ${detail.labels[key] || value}`);
      holder.append(chip);
    });
  };

  const initIndex = (root) => {
    if (root.dataset.csxReady) return;
    root.dataset.csxReady = 'ready';

    root.addEventListener('cs:filter', (event) => {
      updateIndex(root, event.detail.state);
      renderActiveChips(root, event.detail);
    });

    root.addEventListener('click', (event) => {
      const chip = event.target.closest('.csx-chip');
      if (!chip) return;
      window.csContentSystem?.getFilter(root)?.set(chip.dataset.key, 'all');
    });

    // content-system.js runs first and has already applied the initial state
    // (URL params); re-apply silently so this listener sees it.
    window.csContentSystem?.getFilter(root)?.apply({ silent: true });
  };

  // Disabled chips: swallow the click before content-system.js sees it.
  document.addEventListener(
    'click',
    (event) => {
      const chip = event.target.closest?.('[data-cs-filter][aria-disabled="true"]');
      if (!chip) return;
      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  /* ---------------------------------------------------------------------------
     Gallery filmstrip + lightbox
     --------------------------------------------------------------------------- */
  const initGallery = (root) => {
    if (root.dataset.csxReady) return;
    root.dataset.csxReady = 'ready';

    const track = root.querySelector('[data-csx-track]');
    const slides = [...root.querySelectorAll('[data-csx-slide]')];
    const counter = root.querySelector('[data-csx-counter]');
    const prev = root.querySelector('[data-csx-prev]');
    const next = root.querySelector('[data-csx-next]');
    const dots = [...root.querySelectorAll('[data-csx-dot]')];
    const dialog = root.querySelector('[data-csx-lightbox]');
    if (!track || !slides.length) return;

    let current = 0;
    const setCurrent = (index) => {
      current = Math.max(0, Math.min(slides.length - 1, index));
      if (counter) counter.textContent = `${current + 1} / ${slides.length}`;
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === current);
        if (i === current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (prev) prev.disabled = current === 0;
      if (next) next.disabled = current === slides.length - 1;
    };

    // after an arrow/dot click the counter follows the click, not the scroll position
    // (short strips cannot scroll every slide to the start, e.g. Georgia's 540/540/240)
    let lockUntil = 0;
    const goTo = (index) => {
      lockUntil = Date.now() + 700;
      const target = slides[Math.max(0, Math.min(slides.length - 1, index))];
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
      setCurrent(slides.indexOf(target));
    };

    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          if (Date.now() < lockUntil) return;
          const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
          if (atEnd) return setCurrent(slides.length - 1);
          const left = track.scrollLeft;
          let best = 0;
          slides.forEach((slide, i) => {
            const offset = Math.abs(slide.offsetLeft - track.offsetLeft - left);
            const bestOffset = Math.abs(slides[best].offsetLeft - track.offsetLeft - left);
            if (offset < bestOffset) best = i;
          });
          setCurrent(best);
        });
      },
      { passive: true }
    );

    prev?.addEventListener('click', () => goTo(current - 1));
    next?.addEventListener('click', () => goTo(current + 1));
    dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(current + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(current - 1);
      }
    });

    if (dialog && typeof dialog.showModal === 'function') {
      const img = document.createElement('img');
      img.decoding = 'async';
      dialog.querySelector('.csx-lightbox__figure')?.prepend(img);
      const caption = dialog.querySelector('[data-csx-lightbox-caption]');
      let open = 0;
      const show = (index) => {
        open = (index + slides.length) % slides.length;
        const btn = slides[open].querySelector('[data-csx-full]');
        img.src = btn.dataset.csxFull;
        img.alt = btn.querySelector('img')?.alt || '';
        if (caption) caption.textContent = btn.dataset.csxCaption || '';
      };
      slides.forEach((slide, i) =>
        slide.querySelector('[data-csx-full]')?.addEventListener('click', () => {
          show(i);
          dialog.showModal();
        })
      );
      dialog.querySelector('[data-csx-lightbox-prev]')?.addEventListener('click', () => show(open - 1));
      dialog.querySelector('[data-csx-lightbox-next]')?.addEventListener('click', () => show(open + 1));
      dialog.querySelector('[data-csx-lightbox-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => event.target === dialog && dialog.close());
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') show(open + 1);
        else if (event.key === 'ArrowLeft') show(open - 1);
      });
      dialog.addEventListener('close', () => slides[open].querySelector('[data-csx-full]')?.focus());
    }

    setCurrent(0);
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-csx-index]').forEach(initIndex);
    scope.querySelectorAll('[data-csx-gallery]').forEach(initGallery);
  };

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.csCaseStudies = { init };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
